import { Container, Grid, Table, TableBody, TableContainer } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import type { FC } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import InfiniteScrollLoading from '@/components/UI/InfiniteScrollLoading'
import PageContentStickyTableHeader from '@/components/UI/PageContentStickyTableHeader'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import Error from '@/components/UI/Table/Error'
import NoResults from '@/components/UI/Table/NoResults'
import TableRowLoading from '@/components/UI/Table/RowLoading'
import useUser from '@/hooks/useUser'
import { getExecutivesBySquadQuery } from '@/lib/apollo'
import type { TransformedExecutiveBySquadData } from '@/lib/apollo/query/getExecutivesBySquad/dataTransformations/types'
import type { Squad } from '@/utils/squad'

import Row from './Row'
import TableHeader from './TableHeader'

export type Props = {
  collapsedMenu: boolean
  squad: Squad
}

const gridSpacing = 4

const Executives: FC<Props> = ({ collapsedMenu, squad }) => {
  const [infiniteScrollData, setInfiniteScrollData] = useState<
    TransformedExecutiveBySquadData[]
  >([])
  const [totalResults, setTotalResults] = useState<number>(0)

  const { customerId } = useUser()

  const [fetch, { loading, error }] = getExecutivesBySquadQuery.useLazyQuery({
    onCompleted: data => {
      console.log('DATA', data)
      const transformedData = getExecutivesBySquadQuery.toTransformedData(data)

      if (transformedData) {
        setTotalResults(transformedData.total)
        setInfiniteScrollData([...infiniteScrollData, ...transformedData.results])
      }
    },
  })

  const getNextPage = useCallback(() => {
    if (!customerId) {
      return
    }

    fetch({
      variables: {
        customerId,
        squad,
        limit: 100,
        offset: infiniteScrollData.length,
      },
    })
  }, [fetch, customerId, infiniteScrollData.length, squad])

  useEffect(() => {
    if (infiniteScrollData.length === 0) {
      getNextPage()
    }
  }, [getNextPage, customerId, infiniteScrollData.length])

  return (
    <>
      <PageContentStickyTableHeader isNavigationDrawerOpen={!collapsedMenu} yOffset={254}>
        <TableHeader />
      </PageContentStickyTableHeader>
      <Container maxWidth="xl">
        <Grid container spacing={gridSpacing}>
          <PaperedGridItem xs={12}>
            <InfiniteScroll
              dataLength={infiniteScrollData.length}
              next={getNextPage}
              hasMore={infiniteScrollData.length < totalResults}
              loader={<>{!error && <InfiniteScrollLoading />}</>}
              style={{ overflowY: 'hidden' }}
            >
              <TableContainer>
                <Table aria-label="simple table" size="small">
                  <TableHeader />
                  <TableBody>
                    <>
                      {infiniteScrollData.length === 0 && loading ? (
                        <TableRowLoading colSpan={4} />
                      ) : !error && infiniteScrollData.length === 0 ? (
                        <NoResults colSpan={4} />
                      ) : (
                        infiniteScrollData.map(executive => <Row executive={executive} />)
                      )}
                      {error && <Error colSpan={4} />}
                    </>
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          </PaperedGridItem>
        </Grid>
      </Container>
    </>
  )
}

export default Executives
